import {
  ApolloClient,
  ApolloError,
  gql,
  useApolloClient,
} from "@apollo/client";
import {
  DataConnectionId,
  DataSourceTableId,
  ExploreUserJoin,
} from "@hex/common";
import React, { useCallback, useRef, useState } from "react";

import { shallowEqual } from "../../util/index.js";

import {
  TestJoinConfigDocument,
  TestJoinConfigQuery,
  TestJoinConfigQueryVariables,
  TestUniqueKeyConfigDocument,
  TestUniqueKeyConfigQuery,
  TestUniqueKeyConfigQueryVariables,
} from "./exploreConfigureJoinValidation.generated.js";

gql`
  query TestJoinConfig(
    $dataConnectionId: DataConnectionId!
    $baseDataSourceTableId: DataSourceTableId!
    $baseColumnName: String!
    $targetDataSourceTableId: DataSourceTableId!
    $targetColumnName: String!
  ) {
    testJoin(
      dataConnectionId: $dataConnectionId
      baseDataSourceTableId: $baseDataSourceTableId
      baseColumnName: $baseColumnName
      targetDataSourceTableId: $targetDataSourceTableId
      targetColumnName: $targetColumnName
    ) {
      dbError
      hadValidationTimeout
      hasFanout
      hasNoMatches
    }
  }
`;

gql`
  query TestUniqueKeyConfig(
    $dataConnectionId: DataConnectionId!
    $dataSourceTableId: DataSourceTableId!
    $columnName: String!
  ) {
    testUniqueKey(
      dataConnectionId: $dataConnectionId
      dataSourceTableId: $dataSourceTableId
      columnName: $columnName
    ) {
      dbError
      keyNotUnique
    }
  }
`;

const getJoinError = ({
  joinTestResult,
}: {
  joinTestResult: TestJoinConfigQuery["testJoin"] | undefined;
}): React.ReactNode | undefined => {
  if (joinTestResult == null) return undefined;

  if (joinTestResult.hadValidationTimeout)
    return "Timed out while validating. Your join might not be valid and could lead to slow performance.";

  if (joinTestResult.dbError != null)
    return "An error happened in the data connection while testing your configuration.";

  if (joinTestResult.hasNoMatches)
    return "The chosen columns have no matching values. Try matching other columns or choosing a new table to join.";

  return undefined;
};

interface JoinValidationParams {
  dataConnectionId?: DataConnectionId;
  baseDataSourceTableId?: DataSourceTableId;
  targetDataSourceTableId?: DataSourceTableId;
  baseColumnName?: string;
  targetColumnName?: string;
}

interface JoinValidationResult {
  joinTestResult: TestJoinConfigQuery["testJoin"] | undefined;
  validationError: React.ReactNode | undefined;
}

interface UseJoinValidationResult extends JoinValidationResult {
  clearJoinValidation: () => void;
  startJoinValidation: (
    params: JoinValidationParams,
  ) => Promise<undefined | JoinValidationResult>;

  isValidated: boolean;
  isLoading: boolean;
  validationError: JoinValidationResult["validationError"];
  relationshipType: ExploreUserJoin["relationshipType"] | undefined;
}

// Helper function to execute the join validation GraphQL query
async function executeJoinValidationQuery(
  client: ApolloClient<object>,
  variables: TestJoinConfigQueryVariables,
  signal: AbortSignal,
): Promise<TestJoinConfigQuery["testJoin"]> {
  const resp = await client.query<
    TestJoinConfigQuery,
    TestJoinConfigQueryVariables
  >({
    query: TestJoinConfigDocument,
    variables,
    context: { fetchOptions: { signal } },
  });
  return resp.data.testJoin;
}

// Standalone logic function for performing join validation
async function performJoinValidationAsync(
  client: ApolloClient<object>,
  params: JoinValidationParams,
  currentHookValuesForShallowCompare: {
    joinTestResult: TestJoinConfigQuery["testJoin"] | undefined;
    validationError: React.ReactNode | undefined;
  },
  prevVariablesRef: React.MutableRefObject<object | null>,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
): Promise<JoinValidationResult> {
  if (
    params.dataConnectionId == null ||
    params.baseDataSourceTableId == null ||
    params.baseColumnName == null ||
    params.targetDataSourceTableId == null ||
    params.targetColumnName == null
  ) {
    return {
      joinTestResult: undefined,
      validationError: "Missing matching column configuration.",
    };
  }

  const variables = {
    dataConnectionId: params.dataConnectionId,
    baseDataSourceTableId: params.baseDataSourceTableId,
    baseColumnName: params.baseColumnName,
    targetDataSourceTableId: params.targetDataSourceTableId,
    targetColumnName: params.targetColumnName,
  };

  if (shallowEqual(variables, prevVariablesRef.current)) {
    return currentHookValuesForShallowCompare;
  }
  prevVariablesRef.current = variables;

  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  abortControllerRef.current = new AbortController();

  try {
    const apiResult = await executeJoinValidationQuery(
      client,
      variables,
      abortControllerRef.current.signal,
    );
    const maybeError = getJoinError({ joinTestResult: apiResult });
    return { joinTestResult: apiResult, validationError: maybeError };
  } catch (err) {
    const rootErr = err instanceof ApolloError ? err.networkError : err;
    if (rootErr instanceof DOMException && rootErr.name === "AbortError") {
      return {
        joinTestResult: undefined,
        validationError: undefined,
      };
    }
    return {
      joinTestResult: undefined,
      validationError: "Something went wrong while testing the join.",
    };
  }
}

export const useJoinValidation = (): UseJoinValidationResult => {
  const client = useApolloClient();

  const [isValidated, setIsValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<
    React.ReactNode | undefined
  >(undefined);
  const [joinTestResult, setJoinTestResult] =
    useState<TestJoinConfigQuery["testJoin"]>();
  const abortController = useRef<AbortController | null>(null);
  const prevVariables = useRef<object | null>(null);

  // Refs so startJoinValidation can read latest values without being recreated on each result
  const joinTestResultRef = useRef(joinTestResult);
  joinTestResultRef.current = joinTestResult;
  const validationErrorRef = useRef<React.ReactNode | undefined>(
    validationError,
  );
  validationErrorRef.current = validationError;

  const startJoinValidation = useCallback(
    async (params: JoinValidationParams): Promise<JoinValidationResult> => {
      setIsLoading(true);
      setIsValidated(false);
      setValidationError(undefined);

      const currentValsForShallowCompare = {
        joinTestResult: joinTestResultRef.current,
        validationError: validationErrorRef.current,
      };

      const result = await performJoinValidationAsync(
        client,
        params,
        currentValsForShallowCompare,
        prevVariables,
        abortController,
      );

      if (!abortController.current?.signal.aborted) {
        setIsLoading(false);
        setValidationError(result.validationError);
        setJoinTestResult(result.joinTestResult);
        setIsValidated(result.validationError == null);
      }

      return result;
    },
    [client], // stable — reads latest values via refs, not closure
  );

  const clearJoinValidation = useCallback(() => {
    setIsValidated(false);
    setIsLoading(false);
    setValidationError(undefined);
    setJoinTestResult(undefined);
    prevVariables.current = null;
    abortController.current?.abort();
  }, []);

  return {
    isValidated,
    isLoading,
    validationError,
    clearJoinValidation,
    startJoinValidation,
    joinTestResult,
    relationshipType:
      joinTestResult == null
        ? undefined
        : // If we have a timeout, assume the most general relationship type
          // so that we end up generating SQL that works for all cases
          joinTestResult.hasFanout || joinTestResult.hadValidationTimeout
          ? "to-many"
          : "to-one",
  };
};

const getUniqueKeyError = (
  uniqueKeyTestResult: TestUniqueKeyConfigQuery["testUniqueKey"] | undefined,
  columnName: string,
): string | undefined => {
  if (uniqueKeyTestResult == null) return undefined;

  if (uniqueKeyTestResult.dbError != null)
    return "An error happened in the data connection while testing your configuration.";
  if (uniqueKeyTestResult.keyNotUnique)
    return `"${columnName}" has duplicate values. Select a different column with unique values to ensure correct results.`;

  return undefined;
};

interface UniqueKeyValidationParams {
  dataConnectionId?: DataConnectionId;
  dataSourceTableId?: DataSourceTableId;
  columnName?: string;
  onValidationComplete?: (
    isValidated: boolean,
    error: string | undefined,
  ) => void;
}

export interface UniqueKeyValidationResult {
  validationError: string | undefined;
}

export const isUniqueKeyValidationResult = (
  result: UniqueKeyValidationResult | JoinValidationResult | undefined,
): result is UniqueKeyValidationResult => {
  return (
    result != null &&
    "validationError" in result &&
    !("joinTestResult" in result)
  );
};

// Helper function to execute the unique key validation GraphQL query
async function executeUniqueKeyValidationQuery(
  client: ApolloClient<object>,
  variables: TestUniqueKeyConfigQueryVariables,
  signal: AbortSignal,
): Promise<TestUniqueKeyConfigQuery["testUniqueKey"]> {
  const resp = await client.query<
    TestUniqueKeyConfigQuery,
    TestUniqueKeyConfigQueryVariables
  >({
    query: TestUniqueKeyConfigDocument,
    variables,
    context: { fetchOptions: { signal } },
  });
  return resp.data.testUniqueKey;
}

// Standalone logic function for performing unique key validation
async function performUniqueKeyValidationAsync(
  client: ApolloClient<object>,
  params: UniqueKeyValidationParams,
  currentHookValuesForShallowCompare: {
    validationError: string | undefined;
  },
  prevVariablesRef: React.MutableRefObject<object | null>,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
): Promise<UniqueKeyValidationResult> {
  if (
    params.dataConnectionId == null ||
    params.dataSourceTableId == null ||
    params.columnName == null
  ) {
    return { validationError: "No unique key is specified" };
  }

  const variables = {
    dataConnectionId: params.dataConnectionId,
    dataSourceTableId: params.dataSourceTableId,
    columnName: params.columnName,
  };

  if (shallowEqual(variables, prevVariablesRef.current)) {
    return currentHookValuesForShallowCompare;
  }
  prevVariablesRef.current = variables;

  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }
  abortControllerRef.current = new AbortController();

  try {
    const apiResult = await executeUniqueKeyValidationQuery(
      client,
      variables,
      abortControllerRef.current.signal,
    );
    const maybeError = getUniqueKeyError(apiResult, params.columnName);
    return { validationError: maybeError };
  } catch (caughtErr) {
    const rootErr =
      caughtErr instanceof ApolloError ? caughtErr.networkError : caughtErr;
    if (rootErr instanceof DOMException && rootErr.name === "AbortError") {
      return { validationError: undefined }; // As per user's last change for AbortError
    }
    return {
      validationError: "Something went wrong while validating the unique key.",
    };
  }
}

export const useUniqueKeyValidation = () => {
  const client = useApolloClient();

  const [isValidated, setIsValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined,
  );
  const abortController = useRef<AbortController | null>(null);
  const prevVariables = useRef<object | null>(null);

  // Ref so startUniqueKeyValidation can read latest value without being recreated on each result
  const validationErrorRef = useRef(validationError);
  validationErrorRef.current = validationError;

  const startUniqueKeyValidation = useCallback(
    async (
      params: UniqueKeyValidationParams,
    ): Promise<UniqueKeyValidationResult> => {
      setIsLoading(true);
      setIsValidated(false);
      setValidationError(undefined);

      const currentValsForShallowCompare = {
        validationError: validationErrorRef.current,
      };

      const result = await performUniqueKeyValidationAsync(
        client,
        params,
        currentValsForShallowCompare,
        prevVariables,
        abortController,
      );

      if (!abortController.current?.signal.aborted) {
        setIsLoading(false);
        setValidationError(result.validationError);
        const wasSuccessful = result.validationError == null;
        setIsValidated(wasSuccessful);
        params.onValidationComplete?.(wasSuccessful, result.validationError);
      }

      return result;
    },
    [client], // stable — reads latest value via ref, not closure
  );

  const clearUniqueKeyValidation = useCallback(() => {
    setIsValidated(false);
    setIsLoading(false);
    setValidationError(undefined);
    prevVariables.current = null;
    abortController.current?.abort();
  }, []);

  return {
    isValidated,
    isLoading,
    validationError,
    clearUniqueKeyValidation,
    startUniqueKeyValidation,
  };
};
